'use client';

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { AppBar, Collapse, Paper, Menu, MenuItem } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import { MobileContextPills } from '@/components/OmniBox/MobileContextPills';
import { ProductMiniCard } from '@/components/ProductMiniCard';
import { useCompose } from '@/context/ComposeContext';
import { useOmniBox } from '@/context/OmniBoxContext';
import { useUserInfo } from '@/context/UserInfoContext';
import { useLayoutCalculations } from '@/hooks/useLayoutCalculations';
import { useResponsiveLayout } from '@/hooks/useResponsiveLayout';
import {
  DisplayDoc,
  DisplayConceptRef,
  DisplayProduct,
  PublicDocId,
  ThreadTableOfContents,
  UPDATE_COLLECTIONS_ACTION,
  UpdateCollectionsAction,
} from '@/lib/api';
import { API_URL, ASPECT_REVIEW, ASPECT_QUESTION } from '@/lib/consts';
import { useTheme } from '@/theme/ThemeContext';
import { COLOR_MAPPING, resolveColors } from '@/theme/colorPalettes';
import { createElevationStyles } from '@/theme/elevationStyles';
import { LAYOUT_CONFIG, CONTEXT_PANEL_WIDTH } from '@/theme/layoutConstants';

interface LiveContextProps {
  docs: DisplayDoc[];
  tableOfContents?: ThreadTableOfContents;
  isAnalyzingToC?: boolean; // True when ToC is being fetched
}

interface LiveContextContentProps extends LiveContextProps {
  variant: 'toolbar' | 'sidebar';
}

// TODO(2): Get rid of this, just use DisplayProduct directly.
interface ProductInfo {
  cref: string;
  display: string;
  img_url?: string;
  price?: number;
  currency?: string;
  marketplace?: string;
  buy_at_label?: string;
  isStarred: boolean;
}

/** Shows live thread context, table-of-contents navigation, and related products. */
const LiveContext: React.FC<LiveContextProps> = ({ docs, tableOfContents, isAnalyzingToC }) => {
  const { userInfo, reloadUserInfo } = useUserInfo();
  const [productDetails, setProductDetails] = useState<Map<string, DisplayProduct>>(new Map());
  const [showProducts, setShowProducts] = useState(false);
  const [activeTocIndex, setActiveTocIndex] = useState<number | null>(null);
  const [tocAnchorEl, setTocAnchorEl] = useState<null | HTMLElement>(null);
  const { appBarPosition, contextPanelVariant, isNarrowScreen } = useResponsiveLayout();
  const { contextPanelStyle, appBarHeight, contextPanelHeight } = useLayoutCalculations();
  const carouselRef = useRef<HTMLDivElement>(null);
  const thumbnailsRef = useRef<HTMLDivElement>(null);

  // Get theme to determine color mode
  const muiTheme = useMuiTheme();
  const { mode } = useTheme(); // This gives us the resolved mode ('light' or 'dark')
  const { isExpanded } = useOmniBox();
  const { isComposing } = useCompose();

  // Resolve context panel colors
  const contextPanelColors = useMemo(() => {
    const colors = resolveColors(COLOR_MAPPING, mode);
    return {
      bg: colors.contextPanelBg,
      fg: colors.contextPanelFg,
    };
  }, [mode]);

  // Extract unique products from the thread that are in user's collection
  const starredProductCrefs = useMemo(() => {
    if (!userInfo) return [];

    const crefs = new Set<string>();

    docs.forEach((doc) => {
      // Extract products that are the main topic of reviews/questions
      if (
        doc.topic &&
        doc.topic.is_product &&
        (doc.aspect?.cref === ASPECT_REVIEW || doc.aspect?.cref === ASPECT_QUESTION)
      ) {
        // Only add if in user's collection
        const isInCollection = userInfo.collections.some((collection) =>
          collection.products.some((p) => p.cref === doc.topic!.cref)
        );
        if (isInCollection) {
          crefs.add(doc.topic.cref);
        }
      }

      // Also extract any mentioned products
      if (doc.mentioned_concepts) {
        doc.mentioned_concepts.forEach((concept) => {
          if (concept.is_product) {
            // Only add if in user's collection
            const isInCollection = userInfo.collections.some((collection) =>
              collection.products.some((p) => p.cref === concept.cref)
            );
            if (isInCollection) {
              crefs.add(concept.cref);
            }
          }
        });
      }
    });

    return Array.from(crefs);
  }, [docs, userInfo]);

  // Use only starred products
  const displayProductCrefs = starredProductCrefs;

  // Create a stable string key for product crefs to use as dependency
  const productCrefsKey = starredProductCrefs.join(',');

  // Fetch product details for pricing information
  useEffect(() => {
    const fetchProductDetails = async () => {
      const newProductDetails = new Map<string, DisplayProduct>();

      await Promise.all(
        starredProductCrefs.map(async (cref) => {
          try {
            const response = await fetch(`${API_URL}/product/${encodeURIComponent(cref)}`);
            if (response.ok) {
              const product = (await response.json()) as DisplayProduct;
              newProductDetails.set(cref, product);
            }
          } catch (error) {
            console.error(`Failed to fetch product ${cref}:`, error);
          }
        })
      );

      setProductDetails(newProductDetails);
    };

    if (starredProductCrefs.length > 0) {
      fetchProductDetails();
    }
  }, [productCrefsKey, starredProductCrefs]);

  // Set up scroll-based ToC highlighting for both desktop and mobile
  useEffect(() => {
    if (!tableOfContents || tableOfContents.topics.length === 0) return;

    const updateActiveSection = () => {
      // Get all ToC elements with their positions
      const elements = tableOfContents.topics
        .map((topic, index) => {
          // TODO(2): Extract `data-doc-id` into a shared constant so producers
          // (DocTree) and consumers (here) cannot drift.
          const element = document.querySelector(`[data-doc-id="${topic.doc_id}"]`);
          if (!element) return null;

          const rect = element.getBoundingClientRect();
          return {
            index,
            topic,
            top: rect.top,
            bottom: rect.bottom,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null);

      if (elements.length === 0) return;

      // Calculate the reference point - account for fixed headers
      // For desktop with sidebar, use app bar height
      // For mobile/narrow with context panel, use app bar + context panel height
      let referencePoint = appBarHeight + 20; // 20px buffer

      if (contextPanelVariant === 'toolbar') {
        // When context panel is at top/bottom, add its height
        if (appBarPosition === 'top') {
          referencePoint = appBarHeight + contextPanelHeight + 20;
        } else {
          // When app bar is at bottom, context panel is also at bottom
          referencePoint = 20;
        }
      }

      // Find the section that contains our reference point
      let activeIndex = 0;

      // Check if we're scrolled to the very top
      if (window.scrollY < 50) {
        activeIndex = 0;
      } else {
        // Find which section our reference point is in
        for (let i = elements.length - 1; i >= 0; i--) {
          const element = elements[i];
          // If the element starts above our reference point, it's the active one
          if (element.top <= referencePoint) {
            activeIndex = element.index;
            break;
          }
        }
      }

      setActiveTocIndex(activeIndex);
    };

    // Initial update
    updateActiveSection();

    // Throttled scroll handler
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateActiveSection();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateActiveSection, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, [tableOfContents, contextPanelVariant, appBarPosition, appBarHeight, contextPanelHeight]);

  // Handle click outside to close carousel
  useEffect(() => {
    if (!showProducts) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is outside both the carousel and the thumbnail trigger
      if (
        carouselRef.current &&
        !carouselRef.current.contains(event.target as Node) &&
        thumbnailsRef.current &&
        !thumbnailsRef.current.contains(event.target as Node)
      ) {
        setShowProducts(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProducts]);

  // Handle star click to add/remove from collection
  const handleStarClick = async (e: React.MouseEvent, cref: string, isStarred: boolean) => {
    e.preventDefault();
    e.stopPropagation();

    if (!userInfo || !cref) return;

    try {
      const action: UpdateCollectionsAction = isStarred
        ? UPDATE_COLLECTIONS_ACTION.Remove
        : UPDATE_COLLECTIONS_ACTION.Add;
      const response = await fetch(`${API_URL}/user/update_collections`, {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: '',
          action,
          docs: [],
          products: [cref],
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update collection');
      }

      await reloadUserInfo();
    } catch (error) {
      console.error('Error updating collection:', error);
    }
  };

  // Combine product refs with fetched details (starred and recent products)
  const products = useMemo(() => {
    if (!userInfo) return [];

    const productList: ProductInfo[] = [];
    const processedCrefs = new Set<string>();

    // Process all starred product crefs
    starredProductCrefs.forEach((cref) => {
      if (processedCrefs.has(cref)) return;

      // Find product info from docs
      let productInfo: ProductInfo | null = null;

      // Search in docs for product details
      for (const doc of docs) {
        // Check topic
        if (doc.topic?.cref === cref) {
          const details = productDetails.get(cref);
          productInfo = {
            cref: cref,
            display: doc.topic.display,
            img_url: doc.topic.img_url,
            price: details?.price,
            currency: details?.currency,
            marketplace: details?.marketplace,
            buy_at_label: details?.buy_at_label,
            isStarred: starredProductCrefs.includes(cref),
          };
          break;
        }

        // Check mentioned concepts
        if (doc.mentioned_concepts) {
          const concept = doc.mentioned_concepts.find((c) => c.cref === cref);
          if (concept) {
            const details = productDetails.get(cref);
            productInfo = {
              cref: cref,
              display: concept.display,
              img_url: concept.img_url,
              price: details?.price,
              currency: details?.currency,
              marketplace: details?.marketplace,
              buy_at_label: details?.buy_at_label,
              isStarred: starredProductCrefs.includes(cref),
            };
            break;
          }
        }
      }

      if (productInfo) {
        productList.push(productInfo);
        processedCrefs.add(cref);
      }
    });

    return productList;
  }, [docs, productDetails, userInfo, starredProductCrefs]);

  // Handle ToC navigation
  const handleTocClick = useCallback((docId: PublicDocId) => {
    // TODO(2): Extract `data-doc-id` into a shared constant so producers
    // (DocTree) and consumers (here) cannot drift.
    const element = document.querySelector(`[data-doc-id="${docId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setTocAnchorEl(null);
  }, []);

  // Don't render if no products and no table of contents
  if (products.length === 0 && !tableOfContents) {
    return null;
  }

  // Render products carousel for mobile
  const renderProductsCarousel = () => (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        px: 1.5,
      }}
    >
      {products.map((product) => (
        <Box
          key={product.cref}
          sx={{
            minWidth: 160,
            maxWidth: 200,
            flexShrink: 0,
          }}
        >
          <ProductMiniCard
            concept={{
              cref: product.cref,
              display: product.display,
              description: '',
              img_url: product.img_url,
              mention: product.display,
              is_product: true,
            }}
            product={
              product.price
                ? {
                    id: product.cref,
                    marketplace: product.marketplace || '',
                    title: product.display,
                    description: '',
                    vendor: '',
                    image_urls: product.img_url ? [product.img_url] : [],
                    price: product.price,
                    currency: product.currency,
                    tags: [],
                    buy_at_label: product.buy_at_label || product.marketplace || '',
                  }
                : undefined
            }
            variant="inline"
            onStarClick={(cref, isInCollection) => {
              const event = new MouseEvent('click', { bubbles: true, cancelable: true });
              handleStarClick(event as unknown as React.MouseEvent, cref, !isInCollection);
            }}
          />
        </Box>
      ))}
    </Box>
  );

  // Toolbar layout (for narrow screens)
  if (contextPanelVariant === 'toolbar') {
    // Hide on mobile when composing
    if (isComposing) {
      return null;
    }

    const handleTocSelect = (index: number) => {
      if (tableOfContents && tableOfContents.topics[index]) {
        handleTocClick(tableOfContents.topics[index].doc_id);
      }
    };

    // Convert ProductInfo to DisplayConceptRef format
    const productsAsConceptRefs: DisplayConceptRef[] = products.map((product) => ({
      cref: product.cref,
      display: product.display,
      img_url: product.img_url,
      description: '', // Not used in MobileContextPills
      is_product: true,
    }));

    return (
      <MobileContextPills
        topics={tableOfContents?.topics || []}
        currentTopicIndex={activeTocIndex ?? 0}
        onTopicSelect={handleTocSelect}
        starredProducts={productsAsConceptRefs}
        visible={true}
      />
    );
  }

  // Sidebar layout (for wide screens)
  // Hide sidebar when OmniBox is expanded
  if (isExpanded) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        bottom: 80, // Above OmniBox (48px height + 16px margin + 16px extra spacing)
        width: CONTEXT_PANEL_WIDTH,
        backgroundColor: contextPanelColors.bg,
        color: contextPanelColors.fg,
        borderRadius: 3,
        ...createElevationStyles(muiTheme, 3),
        boxShadow: muiTheme.shadows[6],
        border: `1px solid ${muiTheme.palette.divider}`,
        overflowY: 'auto',
        overflowX: 'hidden',
        zIndex: LAYOUT_CONFIG.contextPanel.zIndex,
      }}
    >
      <Box sx={{ p: 2 }}>
        {tableOfContents && tableOfContents.topics.length > 0 ? (
          <Box component="nav" role="navigation" aria-label="Table of contents">
            <List dense>
              {tableOfContents.topics.map((topic, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton
                    onClick={() => handleTocClick(topic.doc_id)}
                    selected={index === activeTocIndex}
                    aria-current={index === activeTocIndex ? 'location' : undefined}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'action.selected',
                      },
                    }}
                  >
                    <ListItemText primary={topic.summary} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        ) : isAnalyzingToC ? (
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Analyzing thread...
          </Typography>
        ) : null}

        {products.length > 0 && (
          <>
            {tableOfContents && <Divider sx={{ my: 2 }} />}
            <Box
              role="region"
              aria-label="Starred products"
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              {products.map((product) => (
                <ProductMiniCard
                  key={product.cref}
                  concept={{
                    cref: product.cref,
                    display: product.display,
                    description: '',
                    img_url: product.img_url,
                    mention: product.display,
                    is_product: true,
                  }}
                  product={
                    product.price
                      ? {
                          id: product.cref,
                          marketplace: product.marketplace || '',
                          title: product.display,
                          description: '',
                          vendor: '',
                          image_urls: product.img_url ? [product.img_url] : [],
                          price: product.price,
                          currency: product.currency,
                          tags: [],
                          buy_at_label: product.buy_at_label || product.marketplace || '',
                        }
                      : undefined
                  }
                  variant="inline"
                  onStarClick={(cref, isInCollection) => {
                    const event = new MouseEvent('click', { bubbles: true, cancelable: true });
                    handleStarClick(event as unknown as React.MouseEvent, cref, !isInCollection);
                  }}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default LiveContext;
